import type { FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import { optimizedImageUrl } from '@/lib/images'
import { prefersReducedMotion } from '@/lib/motion'
import { cn } from '@/lib/utils'

export interface ShaderFrame {
    gl: WebGLRenderingContext
    uniform: (name: string) => WebGLUniformLocation | null
    time: number
    delta: number
    pointer: { x: number, y: number, strength: number }
    width: number
    height: number
}

export interface ShaderPointer {
    x: number
    y: number
    time: number
    kind: 'move' | 'down'
}

interface ShaderCanvasProps {
    fragment: string
    image: string
    className?: string
    maxDpr?: number
    onFrame?: (frame: ShaderFrame) => void
    onPointer?: (pointer: ShaderPointer) => void
}

/** Shared GLSL: precision, common uniforms, object-fit cover mapping and luminance. */
export const SHADER_HEADER = `
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif

varying vec2 vUv;
uniform sampler2D uTexture;
uniform vec2 uResolution;
uniform vec2 uImageSize;
uniform float uTime;
uniform vec2 uPointer;
uniform float uPointerStrength;

vec2 coverUv(vec2 uv) {
    float canvasRatio = uResolution.x / uResolution.y;
    float imageRatio = uImageSize.x / uImageSize.y;
    vec2 scale = canvasRatio > imageRatio ? vec2(1.0, imageRatio / canvasRatio) : vec2(canvasRatio / imageRatio, 1.0);
    return (uv - 0.5) * scale + 0.5;
}

float luma(vec3 color) {
    return dot(color, vec3(0.299, 0.587, 0.114));
}
`

const VERTEX = `
attribute vec2 aPosition;
varying vec2 vUv;

void main() {
    vUv = aPosition * 0.5 + 0.5;
    gl_Position = vec4(aPosition, 0.0, 1.0);
}
`

const compile = (gl: WebGLRenderingContext, type: number, source: string): WebGLShader | null => {
    const shader = gl.createShader(type)

    if (!shader) return null

    gl.shaderSource(shader, source)
    gl.compileShader(shader)

    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        console.error(gl.getShaderInfoLog(shader))
        gl.deleteShader(shader)

        return null
    }

    return shader
}

const createProgram = (gl: WebGLRenderingContext, fragment: string): WebGLProgram | null => {
    const vertexShader = compile(gl, gl.VERTEX_SHADER, VERTEX)
    const fragmentShader = compile(gl, gl.FRAGMENT_SHADER, fragment)

    if (!vertexShader || !fragmentShader) return null

    const program = gl.createProgram()

    if (!program) return null

    gl.attachShader(program, vertexShader)
    gl.attachShader(program, fragmentShader)
    gl.linkProgram(program)

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
        console.error(gl.getProgramInfoLog(program))

        return null
    }

    return program
}

/**
 * A full-bleed WebGL canvas that draws one photograph through a fragment shader.
 * It only renders while on screen, fades in once its texture is ready, and draws a
 * single still frame for visitors who prefer reduced motion.
 */
const ShaderCanvas: FC<ShaderCanvasProps> = ({
    fragment,
    image,
    className,
    maxDpr = 1.5,
    onFrame,
    onPointer
}): ReactNode => {
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const frameCallback = useRef(onFrame)
    const pointerCallback = useRef(onPointer)

    useEffect(() => {
        frameCallback.current = onFrame
        pointerCallback.current = onPointer
    }, [onFrame, onPointer])

    useEffect(() => {
        const canvas = canvasRef.current

        if (!canvas) return

        const gl = canvas.getContext('webgl', {
            alpha: false,
            antialias: false,
            depth: false,
            premultipliedAlpha: false,
            powerPreference: 'high-performance'
        })

        if (!gl) return

        const program = createProgram(gl, fragment)

        if (!program) return

        const buffer = gl.createBuffer()

        gl.bindBuffer(gl.ARRAY_BUFFER, buffer)
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW)

        const position = gl.getAttribLocation(program, 'aPosition')

        gl.enableVertexAttribArray(position)
        gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0)

        const locations = new Map<string, WebGLUniformLocation | null>()

        const uniform = (name: string): WebGLUniformLocation | null => {
            if (!locations.has(name)) locations.set(name, gl.getUniformLocation(program, name))

            return locations.get(name) ?? null
        }

        const texture = gl.createTexture()
        const reduced = prefersReducedMotion()
        const imageSize = { width: 1, height: 1 }
        const pointer = { x: 0.5, y: 0.5, targetX: 0.5, targetY: 0.5, strength: 0, active: false }
        const startedAt = performance.now()

        let hasTexture = false
        let visible = true
        let raf = 0
        let lastFrame = startedAt
        let disposed = false

        const elapsed = (): number => (performance.now() - startedAt) / 1000

        const render = (now: number): void => {
            raf = 0

            if (disposed || !hasTexture) return

            const delta = Math.min((now - lastFrame) / 1000, 0.05)
            const ease = 1 - Math.pow(0.0008, delta)

            lastFrame = now
            pointer.x += (pointer.targetX - pointer.x) * ease
            pointer.y += (pointer.targetY - pointer.y) * ease
            pointer.strength += ((pointer.active ? 1 : 0) - pointer.strength) * ease * 0.6

            const time = (now - startedAt) / 1000

            gl.useProgram(program)
            gl.activeTexture(gl.TEXTURE0)
            gl.bindTexture(gl.TEXTURE_2D, texture)
            gl.uniform1i(uniform('uTexture'), 0)
            gl.uniform2f(uniform('uResolution'), canvas.width, canvas.height)
            gl.uniform2f(uniform('uImageSize'), imageSize.width, imageSize.height)
            gl.uniform1f(uniform('uTime'), time)
            gl.uniform2f(uniform('uPointer'), pointer.x, pointer.y)
            gl.uniform1f(uniform('uPointerStrength'), pointer.strength)

            frameCallback.current?.({
                gl,
                uniform,
                time,
                delta,
                pointer: { x: pointer.x, y: pointer.y, strength: pointer.strength },
                width: canvas.width,
                height: canvas.height
            })

            gl.drawArrays(gl.TRIANGLES, 0, 6)

            if (!reduced && visible && document.visibilityState === 'visible') raf = window.requestAnimationFrame(render)
        }

        const requestRender = (): void => {
            if (!raf && hasTexture && !disposed) raf = window.requestAnimationFrame(render)
        }

        const resize = (): void => {
            const ratio = Math.min(window.devicePixelRatio || 1, maxDpr)
            const width = Math.max(1, Math.round(canvas.clientWidth * ratio))
            const height = Math.max(1, Math.round(canvas.clientHeight * ratio))

            if (canvas.width !== width || canvas.height !== height) {
                canvas.width = width
                canvas.height = height
                gl.viewport(0, 0, width, height)
            }

            requestRender()
        }

        const source = new Image()

        source.decoding = 'async'
        source.onload = (): void => {
            if (disposed) return

            gl.bindTexture(gl.TEXTURE_2D, texture)
            gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true)
            gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, source)
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)

            imageSize.width = source.naturalWidth
            imageSize.height = source.naturalHeight
            hasTexture = true
            canvas.style.opacity = '1'
            resize()
        }

        source.src = optimizedImageUrl(image, 1920)

        const host = canvas.parentElement ?? canvas

        const updatePointer = (event: PointerEvent, kind: ShaderPointer['kind']): void => {
            const rect = canvas.getBoundingClientRect()

            if (!rect.width || !rect.height) return

            const x = (event.clientX - rect.left) / rect.width
            const y = 1 - (event.clientY - rect.top) / rect.height

            pointer.targetX = x
            pointer.targetY = y
            pointer.active = true
            pointerCallback.current?.({ x, y, time: elapsed(), kind })
            requestRender()
        }

        const onMove = (event: PointerEvent): void => updatePointer(event, 'move')
        const onDown = (event: PointerEvent): void => updatePointer(event, 'down')

        const onLeave = (): void => {
            pointer.active = false
        }

        host.addEventListener('pointermove', onMove)
        host.addEventListener('pointerdown', onDown)
        host.addEventListener('pointerleave', onLeave)

        const resizeObserver = new ResizeObserver(resize)

        resizeObserver.observe(canvas)

        const intersection = new IntersectionObserver(([entry]) => {
            visible = entry.isIntersecting

            if (visible) requestRender()
        })

        intersection.observe(canvas)

        const onVisibility = (): void => requestRender()

        document.addEventListener('visibilitychange', onVisibility)

        const onLost = (event: Event): void => {
            event.preventDefault()
            disposed = true
        }

        canvas.addEventListener('webglcontextlost', onLost)

        return () => {
            disposed = true

            if (raf) window.cancelAnimationFrame(raf)

            host.removeEventListener('pointermove', onMove)
            host.removeEventListener('pointerdown', onDown)
            host.removeEventListener('pointerleave', onLeave)
            canvas.removeEventListener('webglcontextlost', onLost)
            document.removeEventListener('visibilitychange', onVisibility)
            resizeObserver.disconnect()
            intersection.disconnect()
            source.onload = null
            gl.deleteTexture(texture)
            gl.deleteBuffer(buffer)
            gl.deleteProgram(program)
        }
    }, [fragment, image, maxDpr])

    return (
        <canvas
            ref={canvasRef}
            aria-hidden='true'
            className={cn('block h-full w-full opacity-0 transition-opacity duration-1000', className)}
        />
    )
}

export default ShaderCanvas
